import { apiService } from './apiService';

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PolicyListItem {
  id: string;
  name: string;
  code: string;
  description: string;
  type: string;
  group: string;
  groupId: string;
  protectionLevel: string;
  status: string;
  assignedAssets: number;
  lastUpdated: string;
  lastUpdatedTime?: string;
  riskLevel: string;
}

export interface PolicyDetail extends PolicyListItem {
  appliesTo: string[];
  promptFirewall: Record<string, unknown>;
  dataProtection: Record<string, unknown>;
  resourceGovernance: Record<string, unknown>;
  authorizationConditions: Array<Record<string, string>>;
  approvalRules: Record<string, unknown>;
  overrides: Record<string, unknown>;
  audit: Record<string, unknown>;
  outputValidation: Record<string, unknown>;
}

export interface PolicyStats {
  total: number;
  active: number;
  draft: number;
  inactive: number;
  groups: number;
  unassignedGroups: number;
  assignments: number;
  overrides: number;
  byType: Array<{ label: string; count: number }>;
  trendVsLastMonthPct: number;
}

export interface PolicyActivity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
}

export interface GroupItem {
  id: string;
  name: string;
  description: string;
  policyCount: number;
  assignedAssets: number;
  riskLevel: string;
  owner: string;
  ownerId: string;
  lastUpdated: string;
}

export interface AssignmentItem {
  id: string;
  resourceId: string;
  resourceName: string;
  resourceType: string;
  department: string;
  assignedPolicy: string;
  policyId: string;
  inheritanceSource: string;
  overrideStatus: string;
  lastEvaluated: string;
}

export interface OverrideItem {
  id: string;
  scope: string;
  scopeType: string;
  policy: string;
  policyId: string;
  exceptionType: string;
  reason: string;
  approvedBy: string;
  expiryDate: string;
  status: string;
  riskLevel: string;
}

export interface LibraryTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  policies: number;
  downloads: number;
  policyDefinitions?: Array<{
    id: string;
    name: string;
    code: string;
    description: string;
    type: string;
    protectionLevel: string;
    riskLevel: string;
  }>;
}

export interface AuditLogItem {
  id: string;
  timestamp: string;
  user: string;
  resource: string;
  policy: string;
  decision: string;
  riskLevel: string;
  toolInvoked: string;
  modelUsed: string;
  details: string;
}

export interface AuditStats {
  totalEvents: number;
  blockedRequests: number;
  approvalsPending: number;
  maskActions: number;
}

export interface GovernanceSettings {
  enforcePoliciesGlobally: boolean;
  defaultPolicyId: string;
  autoAssignNewResources: boolean;
  blockUnassignedResources: boolean;
  cascadeDepartmentPolicies: boolean;
  requireApprovalForOverrides: boolean;
  maxOverrideDurationDays: number;
  notifyOnPolicyViolation: boolean;
  notifyOnHighRisk: boolean;
  enableRealTimeMonitoring: boolean;
  emailNotifications: string;
  slackWebhook: string;
  policyDecisionRetentionDays: number;
  auditLogRetentionDays: number;
}

export interface TestPolicyResult {
  decision: string;
  riskLevel: string;
  reason: string;
  matchedRules: string[];
}

const BASE = '/api/ai-governance';

export const aiGovernanceService = {
  // Policies
  getPolicies: (params: Record<string, string | number | undefined> = {}) =>
    apiService.get(`${BASE}/policies`, params, 'backend') as Promise<PagedResult<PolicyListItem>>,

  getPolicy: (id: string) =>
    apiService.get(`${BASE}/policies/${id}`, {}, 'backend') as Promise<PolicyDetail>,

  createPolicy: (dto: unknown) =>
    apiService.post(`${BASE}/policies`, dto, 'backend') as Promise<PolicyDetail>,

  updatePolicy: (id: string, dto: unknown) =>
    apiService.put(`${BASE}/policies/${id}`, dto, 'backend') as Promise<PolicyDetail>,

  deletePolicy: (id: string) =>
    apiService.delete(`${BASE}/policies/${id}`, 'backend'),

  patchPolicyStatus: (id: string, status: string) =>
    apiService.patch(`${BASE}/policies/${id}/status`, { status }, 'backend'),

  importPolicy: (policy: unknown) =>
    apiService.post(`${BASE}/policies/import`, { policy }, 'backend') as Promise<PolicyDetail>,

  getPolicyStats: () =>
    apiService.get(`${BASE}/policies/stats`, {}, 'backend') as Promise<PolicyStats>,

  getPolicyActivity: (limit = 10) =>
    apiService.get(`${BASE}/policies/activity`, { limit }, 'backend') as Promise<PolicyActivity[]>,

  testPolicy: (id: string, dto: { prompt: string; userRole: string; department: string; resourceId: string }) =>
    apiService.post(`${BASE}/policies/${id}/test`, dto, 'backend') as Promise<TestPolicyResult>,

  // Groups
  getGroups: (params: Record<string, string | number | undefined> = {}) =>
    apiService.get(`${BASE}/groups`, params, 'backend') as Promise<PagedResult<GroupItem>>,

  getGroup: (id: string) =>
    apiService.get(`${BASE}/groups/${id}`, {}, 'backend') as Promise<GroupItem>,

  createGroup: (dto: { name: string; description?: string; ownerId?: string; ownerName?: string; riskLevel?: string }) =>
    apiService.post(`${BASE}/groups`, dto, 'backend') as Promise<GroupItem>,

  updateGroup: (id: string, dto: { name: string; description?: string; ownerId?: string; ownerName?: string; riskLevel?: string }) =>
    apiService.put(`${BASE}/groups/${id}`, dto, 'backend') as Promise<GroupItem>,

  deleteGroup: (id: string) =>
    apiService.delete(`${BASE}/groups/${id}`, 'backend'),

  assignGroup: (id: string, dto: { resourceIds: string[]; resourceType: string; policyIds?: string[]; mode: string }) =>
    apiService.post(`${BASE}/groups/${id}/assign`, dto, 'backend'),

  // Assignments
  getAssignments: (params: Record<string, string | number | undefined> = {}) =>
    apiService.get(`${BASE}/assignments`, params, 'backend') as Promise<PagedResult<AssignmentItem>>,

  createAssignment: (dto: unknown) =>
    apiService.post(`${BASE}/assignments`, dto, 'backend') as Promise<AssignmentItem>,

  updateAssignment: (id: string, dto: { policyId: string; inheritanceSource?: string }) =>
    apiService.put(`${BASE}/assignments/${id}`, dto, 'backend') as Promise<AssignmentItem>,

  deleteAssignment: (id: string) =>
    apiService.delete(`${BASE}/assignments/${id}`, 'backend'),

  bulkAssign: (dto: unknown) =>
    apiService.post(`${BASE}/assignments/bulk`, dto, 'backend'),

  // Overrides
  getOverrides: (params: Record<string, string | number | undefined> = {}) =>
    apiService.get(`${BASE}/overrides`, params, 'backend') as Promise<PagedResult<OverrideItem>>,

  createOverride: (dto: unknown) =>
    apiService.post(`${BASE}/overrides`, dto, 'backend') as Promise<OverrideItem>,

  revokeOverride: (id: string, reason: string) =>
    apiService.post(`${BASE}/overrides/${id}/revoke`, { reason }, 'backend') as Promise<OverrideItem>,

  approveOverride: (id: string, comment?: string) =>
    apiService.post(`${BASE}/overrides/${id}/approve`, { comment }, 'backend') as Promise<OverrideItem>,

  // Library
  getTemplates: (params: Record<string, string | undefined> = {}) =>
    apiService.get(`${BASE}/library/templates`, params, 'backend') as Promise<LibraryTemplate[]>,

  getTemplate: (id: string) =>
    apiService.get(`${BASE}/library/templates/${id}`, {}, 'backend') as Promise<LibraryTemplate>,

  importTemplate: (id: string, dto: { targetGroupId?: string; status?: string; importAll?: boolean; policyIds?: string[] }) =>
    apiService.post(`${BASE}/library/templates/${id}/import`, dto, 'backend') as Promise<PolicyDetail[]>,

  // Audit
  getAuditLogs: (params: Record<string, string | number | undefined> = {}) =>
    apiService.get(`${BASE}/audit-logs`, params, 'backend') as Promise<PagedResult<AuditLogItem>>,

  getAuditLog: (id: string) =>
    apiService.get(`${BASE}/audit-logs/${id}`, {}, 'backend') as Promise<AuditLogItem>,

  getAuditStats: (window = '24h') =>
    apiService.get(`${BASE}/audit-logs/stats`, { window }, 'backend') as Promise<AuditStats>,

  exportAuditLogs: async (params: Record<string, string | undefined> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) qs.set(k, v);
    });
    const q = qs.toString();
    return apiService.getBlob(`${BASE}/audit-logs/export${q ? `?${q}` : ''}`, 'backend');
  },

  // Settings
  getSettings: () =>
    apiService.get(`${BASE}/settings`, {}, 'backend') as Promise<GovernanceSettings>,

  saveSettings: (dto: GovernanceSettings) =>
    apiService.put(`${BASE}/settings`, dto, 'backend') as Promise<GovernanceSettings>,
};
